import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CreditCard, ShieldCheck, AlertCircle } from 'lucide-react';

/**
 * Local mock of the PayTech checkout page.
 * Activated when PAYTECH_PROVIDER=mock on the backend.
 * Displays Success / Cancel buttons that call our mock webhook helpers.
 */
const MockPaymentPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ref = params.get('ref') || '';
  const amount = params.get('amount') || '0';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ref) setError('Référence de paiement manquante.');
  }, [ref]);

  const callMock = async (action) => {
    setBusy(true);
    setError('');
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/payment/mock/${action}`;
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ref_command: ref }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || 'Erreur serveur');
      }
      if (action === 'complete') {
        navigate(`/payment-success?ref=${ref}`);
      } else {
        navigate(`/payment-cancel?ref=${ref}`);
      }
    } catch (e) {
      setError(e.message || 'Échec de la simulation');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-white shadow-2xl" data-testid="mock-payment-card">
        <div className="flex items-center gap-3 mb-6 pb-6 border-b">
          <div className="p-3 bg-amber-100 rounded-xl">
            <CreditCard className="text-amber-600" size={28} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Mode simulation</h1>
            <p className="text-xs text-slate-500">Sandbox PayTech (mock)</p>
          </div>
        </div>

        <div className="space-y-4 mb-8">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Référence</span>
            <span className="font-mono text-slate-900" data-testid="mock-ref">{ref || '—'}</span>
          </div>
          <div className="flex justify-between text-base">
            <span className="text-slate-600">Montant</span>
            <span className="font-bold text-slate-900" data-testid="mock-amount">
              {parseInt(amount, 10).toLocaleString('fr-FR')} F CFA
            </span>
          </div>
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <ShieldCheck className="text-amber-600 mt-0.5 shrink-0" size={16} />
            <p className="text-xs text-amber-800">
              Ceci est une page de simulation. Aucun paiement réel n'est effectué.
              Sera remplacée par la vraie page PayTech une fois les clés API configurées.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="text-red-600 mt-0.5 shrink-0" size={16} />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={() => callMock('complete')}
            disabled={busy || !ref}
            size="lg"
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            data-testid="mock-success-btn"
          >
            {busy ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
            Simuler un paiement réussi
          </Button>
          <Button
            onClick={() => callMock('cancel')}
            disabled={busy || !ref}
            variant="outline"
            size="lg"
            className="w-full"
            data-testid="mock-cancel-btn"
          >
            Annuler le paiement
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default MockPaymentPage;
